import toast from 'react-hot-toast';

/**
 * Utilidades para notificaciones toast centralizadas
 */

// Configuración personalizada
const toastConfig = {
  success: {
    icon: '✅',
    style: {
      background: '#0096FF',
      color: '#FFFFFF',
      fontWeight: '500',
    },
  },
  error: {
    icon: '❌',
    style: {
      background: '#FF4444',
      color: '#FFFFFF',
      fontWeight: '500',
    },
  },
  warning: {
    icon: '⚠️',
    style: {
      background: '#FFA500',
      color: '#FFFFFF',
      fontWeight: '500',
    },
  },
  info: {
    icon: 'ℹ️',
    style: {
      background: '#363636',
      color: '#FFFFFF',
      fontWeight: '400',
    },
  },
};

export const showToast = {
  /**
   * Toast de éxito
   */
  success: (mensaje) => {
    toast.success(mensaje, toastConfig.success);
  },

  /**
   * Toast de error
   */
  error: (mensaje) => {
    toast.error(mensaje, toastConfig.error);
  },

  /**
   * Toast de advertencia
   */
  warning: (mensaje) => {
    toast(mensaje, toastConfig.warning);
  },

  /**
   * Toast informativo
   */
  info: (mensaje) => {
    toast(mensaje, toastConfig.info);
  },

  /**
   * Toast de promesa (para operaciones asíncronas)
   * @param {Promise} promesa - Promesa a ejecutar
   * @param {Object} mensajes - Mensajes para cada estado
   * @param {string} mensajes.loading - Mensaje mientras carga
   * @param {string} mensajes.success - Mensaje de éxito
   * @param {string} mensajes.error - Mensaje de error
   */
  promise: (promesa, mensajes) => {
    return toast.promise(promesa, {
      loading: mensajes.loading || 'Procesando...',
      success: mensajes.success || 'Operación completada',
      error: mensajes.error || 'Error en la operación',
    });
  },

  /**
   * Toast personalizado
   */
  custom: (mensaje, opciones = {}) => {
    toast(mensaje, opciones);
  },
};

/**
 * Toasts específicos para operaciones comunes
 */
export const toastMessages = {
  // Sesiones
  sesionReservada: () => showToast.success('✅ Sesión reservada exitosamente'),
  sesionCancelada: () => showToast.warning('🔴 Sesión cancelada'),
  sesionAsignada: () => showToast.success('✅ Sesión asignada correctamente'),
  errorReservar: () => showToast.error('❌ Error al reservar sesión'),
  errorCancelar: () => showToast.error('❌ Error al cancelar sesión'),
  sinCupos: () => showToast.warning('⚠️ No hay cupos disponibles'),
  sesionDuplicada: () => showToast.warning('⚠️ Ya tienes una sesión en este horario'),

  // Pagos
  pagoRegistrado: () => showToast.success('💰 Pago registrado. Pendiente de confirmación'),
  pagoConfirmado: () => showToast.success('✅ Pago confirmado correctamente'),
  pagoRechazado: () => showToast.error('❌ Pago rechazado'),
  errorPago: () => showToast.error('❌ Error al procesar el pago'),

  // Suscripciones
  suscripcionCreada: () => showToast.success('✅ Suscripción creada correctamente'),
  suscripcionActivada: () => showToast.success('✅ Suscripción activada'),
  suscripcionActualizada: () => showToast.success('✅ Suscripción actualizada'),
  suscripcionCancelada: () => showToast.warning('🔴 Suscripción cancelada'),
  suscripcionRenovada: () => showToast.success('🔄 Suscripción renovada exitosamente'),
  suscripcionPorVencer: () => showToast.warning('⏰ Tu suscripción está por vencer'),
  suscripcionVencida: () => showToast.error('🔴 Tu suscripción ha vencido'),
  sinSesionesDisponibles: () => showToast.warning('⚠️ No tienes sesiones disponibles'),

  // Planes de Suscripción
  planCreado: () => showToast.success('✅ Plan creado correctamente'),
  planActualizado: () => showToast.success('✅ Plan actualizado'),
  planEliminado: () => showToast.success('🗑️ Plan eliminado'),

  // Planificaciones
  planificacionCreada: () => showToast.success('📋 Planificación creada'),
  planificacionActualizada: () => showToast.success('📝 Planificación actualizada'),
  planificacionEliminada: () => showToast.success('🗑️ Planificación eliminada'),
  errorPlanificacion: () => showToast.error('❌ Error en la planificación'),

  // Rutinas de Entrenamiento
  rutinaCreada: () => showToast.success('📋 Rutina creada correctamente'),
  rutinaActualizada: () => showToast.success('✅ Rutina actualizada'),
  rutinaEliminada: () => showToast.success('🗑️ Rutina eliminada'),

  // Usuarios
  usuarioCreado: () => showToast.success('👤 Usuario creado correctamente'),
  usuarioActualizado: () => showToast.success('✅ Usuario actualizado'),
  usuarioEliminado: () => showToast.success('🗑️ Usuario eliminado'),
  errorUsuario: () => showToast.error('❌ Error al gestionar usuario'),

  // Autenticación
  loginExitoso: () => showToast.success('✅ Bienvenido'),
  logoutExitoso: () => showToast.success('👋 Sesión cerrada'),
  errorLogin: () => showToast.error('❌ Credenciales inválidas'),
  sesionExpirada: () => showToast.warning('⏰ Tu sesión ha expirado'),
  passwordCambiado: () => showToast.success('🔐 Contraseña actualizada'),

  // Ejercicios
  ejercicioCreado: () => showToast.success('💪 Ejercicio creado'),
  ejercicioActualizado: () => showToast.success('✅ Ejercicio actualizado'),
  ejercicioEliminado: () => showToast.success('🗑️ Ejercicio eliminado'),
  errorEjercicio: () => showToast.error('❌ Error en el ejercicio'),

  // Genéricos
  guardadoExitoso: () => showToast.success('✅ Guardado correctamente'),
  eliminadoExitoso: () => showToast.success('🗑️ Eliminado correctamente'),
  errorGenerico: () => showToast.error('❌ Ocurrió un error'),
  errorConexion: () => showToast.error('🌐 Error de conexión'),
  camposInvalidos: () => showToast.warning('⚠️ Por favor completa todos los campos'),
  operacionNoPermitida: () => showToast.error('🚫 No tienes permisos para esta operación'),
};

/**
 * Helper para manejar errores de API con toast
 * @param {Error} error - Error de la API
 * @param {string} mensajeDefault - Mensaje por defecto si no hay mensaje del servidor
 */
export const handleApiError = (error, mensajeDefault = 'Ocurrió un error') => {
  const mensaje = error.response?.data?.message || error.message || mensajeDefault;
  showToast.error(mensaje);
};

/**
 * Helper para operaciones async con toast automático
 * @param {Function} operacion - Función async a ejecutar
 * @param {Object} mensajes - Mensajes para cada estado
 */
export const withToast = async (operacion, mensajes = {}) => {
  try {
    const resultado = await showToast.promise(operacion(), {
      loading: mensajes.loading || 'Procesando...',
      success: mensajes.success || 'Operación completada',
      error: mensajes.error || 'Error en la operación',
    });
    return resultado;
  } catch (error) {
    throw error;
  }
};

export default showToast;
